import React from 'react';
import PropTypes from 'prop-types';
import { has, get, orderBy } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import { SanitizeHtml, buildThumbnailUrl } from '@bd/bdcore-utilities';
import LightBox from '@bd/bdui-lightbox';
import { colors, typography } from '../../styles';
import HomeCard from '../../components/HomeCard';
import LightboxPhotoList from '../../components/LightBoxPhotoList';

const styles = css`
  .DetailDescription_wrapper {
    font-family: ${typography.Serif};
    background-size: cover;
    background-position: center center;
    background-image: url('/images/comm-detail-description-bg-fpo.jpg');
    padding: 30px;
    position: relative;
    @media (min-width: 992px) {
      padding: 90px 60px;
    }
    &:before {
      content: '';
      background-color: rgba(63,63,63, .5);
      position: absolute;
      display: block;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
    }
    &:after {
      content: '';
      background-color: rgba(53,53,53, .9);
      position: absolute;
      display: block;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      z-index: 2;
      @media (min-width: 992px) {
        right: 25%;
      }
    }
  }
  .DetailDescription_inner {
    z-index: 3;
    position: relative;
  }
  .DetailDescription_preTitle {
    font-weight: 200;
    font-size: 40px;
    color: #FFFFFF;
    @media (min-width: 992px) {
      font-size: 80px;
    }
  }
  .DetailDescription_title {
    font-weight: 800;
    font-size: 30px;
    line-height: 40px;
    color: ${colors.color3Darker};
    text-transform: uppercase;
    position: relative;
    margin: 0 0 60px 0;
    &:after {
      width: 125px;
      height: 3px;
      position: absolute;
      display: block;
      content: '';
      bottom: -30px;
      background-color: ${colors.color2};
    }
  }
  .DetailDescription_body {
    font-family: ${typography.SanSerif};
    font-weight: 200;
    font-size: 18px;
    color: #FFFFFF;
    line-height: 37px;
  }
  .DetailDescription_media {
    display:none;
    @media (min-width: 992px) {
      display: block;
      background-size: cover;
      background-position: center center;
      background-image: url('/images/comm-description-media-fpo.jpg');
    }
  }
  .ZinckVirtualTour_wrapper {
    display: inline-block;
    width: 100%;
    max-width: 1200px;
    overflow: hidden;
    position: relative;
    overflow-y: hidden;
    &:before {
      padding-top: 56.25%;
      content: '';
      display: block;
      position: relative;
    }
    iframe {
      position: absolute;
      left: 0;
      right: 0;
      top: -123px;
      width: 100%;
      height: calc(100% + 123px);
      overflow: hidden;
    }
  }
  .DetailSection {
    padding: 45px 0;
    @media (min-width: 992px) {
      padding: 90px 0;
    }
    img {
      max-width: 100%;
    }
    .DetailSection_heading {
      text-transform: uppercase;
      font-family: ${typography.Serif};
      font-size: 24px;
      font-weight: 300;
      color: ${colors.color5};
      position: relative;
      text-align: center;
      margin: 0 0 60px 0;
      @media (min-width: 992px) {
        margin: 0 0 70px 0;
        font-size: 36px;
      }
      span {
        font-weight: 900;
        color: ${colors.color5Darker};
      }
      &:after {
        content: '';
        width: 180px;
        height: 5px;
        bottom: -30px;
        background-color: ${colors.color2};
        left: 0;
        right: 0;
        margin: 0 auto;
        position: absolute;
        display: block;
      }
    }
  }
  .lighter {
    font-weight: 400;
  }
`;

function HomeDetailPage({ home, homes, widget }) {
  const background = {};
  if (has(home, 'photos[0].contentUrl')) {
    background.backgroundImage = get(home, 'photos[0].contentUrl');
  } else {
    background.backgroundImage = get(widget, 'default_image.contentUrl');
  }
  return (
    <div className={styles}>
      {
        get(home, 'description') &&
        <div className="DetailDescription" id="description">
          <div className="DetailDescription_wrapper">
            <div className="DetailDescription_inner row">
              <div className="DetailDescription_content col-lg-7">
                <h4 className="DetailDescription_title">
                  <span className="DetailDescription_preTitle">Home</span><br />
                  Description
                </h4>
                <div className="DetailDescription_body" dangerouslySetInnerHTML={SanitizeHtml(get(home, 'description'))} />
              </div>
              <div className="DetailDescription_media col-lg-4 ml-auto" style={{ backgroundImage: `url(${buildThumbnailUrl(background.backgroundImage, { width: 800, height: 800 })})` }} />
            </div>
          </div>
        </div>
      }
      {
        get(home, 'tourUrl') &&
        <div className="DetailSection text-center" id="tour">
          <h3 className="DetailSection_heading"><span>Virtual</span> Tour</h3>
          <div className="ZinckVirtualTour_wrapper">
            <iframe
              src={get(home, 'tourUrl')}
              frameBorder="0"
              title="Virtual Tour"
              scrolling="no"
            />
          </div>
        </div>
      }
      {
        get(home, 'floorplanPhotos', []).length > 0 &&
        <div className="DetailSection text-center" id="plans">
          <h3 className="DetailSection_heading"><span>Floor</span> Plans</h3>
          <div className="container">
            <LightBox
              items={get(home, 'floorplanPhotos', [])}
            >
              <div className="row justify-content-center">
                {
                  get(home, 'floorplanPhotos', []).map((photo, index) => (
                    <div className="col-lg-4" key={photo.contentUrl}>
                      <img data-index={index} src={get(photo, 'contentUrl')} alt={get(photo, 'caption')} />
                    </div>
                  ))
                }
              </div>
            </LightBox>
          </div>
        </div>
      }
      <div className="DetailSection text-center" id="gallery">
        <h3 className="DetailSection_heading"><span>Photo</span> Gallery</h3>
        <div className="container">
          <LightboxPhotoList
            show={8}
            photos={get(home, 'photos', []).concat(get(home, 'elevationPhotos', []))}
          />
        </div>
      </div>
      {
        homes.filter(h => home !== h).length > 0 &&
        <div className="DetailSection text-center" id="other-homes">
          <h3 className="DetailSection_heading"><span>Nearby</span> Move-In Homes</h3>
          <div className="container">
            <div className="row py-4 justify-content-center">
              {
                orderBy(homes, 'sqft', 'desc').filter(h => home !== h).map(otherHome => (
                  <div key={otherHome._id} className="col-lg-6 col-xl-4">
                    <HomeCard home={otherHome} />
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      }
    </div>
  );
}

HomeDetailPage.propTypes = {
  home: PropTypes.object.isRequired,
  homes: PropTypes.arrayOf(PropTypes.object),
  widget: PropTypes.object.isRequired,
};

HomeDetailPage.defaultProps = {
  community: undefined,
  homes: [],
};

export default HomeDetailPage;



// WEBPACK FOOTER //
// ./src/pages/HomeDetail/HomeDetailPage.js